/**
 * Shared types + tier mapping for the /brands index page.
 *
 * The database stores `tier` as one of `mainstream | luxury | value | budget
 * | niche` (878 of 1,158 brands are NULL; only ~270 are tiered). The handoff
 * design uses the friendlier label "Popular" instead of "Mainstream", and
 * filters the index by that same set of buckets. We translate at the UI
 * boundary so the DB stays canonical and the URL / display labels match
 * the design system.
 */

/** Display tier slug used by the UI (filter pills, tier pill labels). */
export type DisplayTier = "popular" | "luxury" | "value" | "budget" | "niche"

/** DB tier value stored on `brands.tier`. NULL is common (untiered). */
export type DbTier = "mainstream" | "luxury" | "value" | "budget" | "niche" | null

const DISPLAY_TIER_VALUES: ReadonlySet<DisplayTier> = new Set([
  "popular",
  "luxury",
  "value",
  "budget",
  "niche",
])

/**
 * Mapping from DB tier → UI display tier. NULL/unknown values return NULL
 * so the TierPill stays hidden and the brand only matches the "All" filter.
 * Defensive: the DB column is `TEXT` not an enum, so a typo'd value would
 * otherwise crash `PALETTE[tier]` lookups downstream.
 */
export function dbTierToDisplay(t: string | null | undefined): DisplayTier | null {
  if (!t) return null
  const lower = t.toLowerCase().trim()
  if (lower === "mainstream") return "popular"
  if (DISPLAY_TIER_VALUES.has(lower as DisplayTier)) return lower as DisplayTier
  return null
}

/** UI filter slug → DB tier value the brand row must match. */
export function displayFilterToDb(f: DisplayTier): DbTier {
  if (f === "popular") return "mainstream"
  return f
}

/** Display-side brand record used everywhere on the index page. */
export interface IndexBrand {
  /** url slug → /brands/[slug] */
  slug: string
  /** display name */
  name: string
  /** brand logo URL — supabase / external — may be empty string */
  logo_path: string | null
  /**
   * FRIDGE-1495: the logo file's width over its height (lib/brand-logo-sizes.ts,
   * read on the server). Absent when the size is not recorded; logo-shape.ts
   * then keeps the standard tile.
   */
  logoAspect?: number
  /** translated tier for UI rendering (may be null) */
  tier: DisplayTier | null
  /** `true` if `brands.is_partner` — featured in the hero stack + logo bar */
  isPartner: boolean
  /** Curated ordering for the featured-logo strip. Lower = surfaced first.
   *  Samsung=1, LG=2, Whirlpool=3, GE=4, Frigidaire=5, KitchenAid=6, etc. */
  displayOrder: number
  /** count of active products keyed by `products.brand` */
  productCount: number
  /** lowest `lowest_price_cents / 100` across active products */
  minPrice: number | null
  /** highest `lowest_price_cents / 100` across active products */
  maxPrice: number | null
  /** distinct `products.type` values seen for this brand */
  types: string[]
}

/** Sort modes available in the toolbar. */
export type Sort = "alphabetical" | "popularity" | "price-low" | "price-high"

/** Tier filter (includes "all" sentinel). */
export type TierFilter = "all" | DisplayTier

/** Ordered list of filter options matching the handoff. */
export const TIER_FILTERS: ReadonlyArray<{ key: TierFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "popular", label: "Popular" },
  { key: "luxury", label: "Luxury" },
  { key: "value", label: "Value" },
  { key: "budget", label: "Budget" },
  { key: "niche", label: "Niche" },
]

/** Ordered list of sort options matching the handoff. */
export const SORT_OPTIONS: ReadonlyArray<{ key: Sort; label: string }> = [
  { key: "alphabetical", label: "A → Z" },
  { key: "popularity", label: "Most products" },
  { key: "price-low", label: "Price: low to high" },
  { key: "price-high", label: "Price: high to low" },
]

/** Bucket key for grouping a brand by first letter of name. Non-letters → `#`. */
export function letterKey(name: string): string {
  const ch = (name || "").charAt(0).toUpperCase()
  return /[A-Z]/.test(ch) ? ch : "#"
}

/** Deterministic accent color for letter-fallback tiles. Cycles through 3 tints. */
export function accentColorFor(slug: string): string {
  let h = 0
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0
  const colors = ["#ff9951", "#0469f0", "#9ca3af"]
  return colors[Math.abs(h) % colors.length]
}
